import { useCallback, useEffect, useRef, useState } from "react";
import { Search, Plus, Loader2, MapPin } from "lucide-react";
import api from "../../../lib/api";
import { formatPrice } from "../../../lib/formatPrice";
import { useOnClickOutside } from "../hooks/useOnClickOutside";
import { useLabBooking } from "../context/LabBookingContext";
import { POPULAR_CHIPS } from "../mock/mockData";
import LocationModal from "./LocationModal";

// Backend picks one (cheapest) lab per popular/searched package just to
// return a representative price — mobile never surfaces that lab name here
// since a package isn't tied to one lab, so this drops it too.
const SearchResultRow = ({ result, selected, onAdd }) => (
  <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0">
    <div className="w-8 h-8 rounded-full bg-[#EEF3F2] flex items-center justify-center text-[14px] shrink-0">
      {result.kind === "package" ? "📦" : "🧪"}
    </div>
    <div className="flex-1 min-w-0">
      <p className="text-[13px] font-semibold text-[#191C1D] truncate">{result.name}</p>
      <p className="text-[11px] text-[#6B7A99] truncate">
        {result.kind === "package"
          ? (result.test_count ? `${result.test_count} Tests Included` : "")
          : result.test_count
          ? `${result.test_count} parameters`
          : result.body_part || result.disease_name || ""}
      </p>
    </div>
    <p className="text-[13px] font-bold text-[#191C1D] shrink-0">
      From ₹{formatPrice(result.from_price)}
    </p>
    <button
      onClick={onAdd}
      className={`flex items-center gap-1 text-[12px] font-semibold px-3 py-1.5 rounded-full transition shrink-0 ${
        selected
          ? "bg-[#EEF3F2] text-[#13B58E]"
          : "bg-[#13B58E] text-white hover:bg-[#0fa07d]"
      }`}
    >
      {selected ? "Added" : <><Plus size={13} /> Add</>}
    </button>
  </div>
);

const LabSearchBar = () => {
  const [query, setQuery]     = useState("");
  const [open, setOpen]       = useState(false);
  const [tests, setTests]     = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const containerRef          = useRef(null);
  const { addTest, isTestSelected, removeTest, location } = useLabBooking();

  const [showLocationModal, setShowLocationModal] = useState(false);

  const closeDropdown = useCallback(() => setOpen(false), []);
  useOnClickOutside(containerRef, closeDropdown);

  useEffect(() => {
    if (!query.trim()) { setTests([]); setPackages([]); return; }
    setLoading(true);
    setError(null);
    const timer = setTimeout(() => {
      const name = query.trim();
      Promise.all([
        api.post("/user/lab/master-test",    { name, page: 1 }),
        api.post("/user/lab/master-package", { name, page: 1 }),
      ])
        .then(([testRes, packageRes]) => {
          setTests((testRes.data?.data?.items || []).map((r) => ({ ...r, kind: "test" })));
          setPackages((packageRes.data?.data?.items || []).map((r) => ({ ...r, kind: "package" })));
        })
        .catch(() => setError("Couldn't load results, try again."))
        .finally(() => setLoading(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [query]);

  const results = [...tests, ...packages];

  const toSelectionItem = (result) => {
    if (result.kind === "package") {
      return { name: result.name, test_count: result.test_count, is_package: 1, from_price: result.from_price, lab_name: result.lab_name };
    }
    return result.source === "unmapped"
      ? { test_name: result.name, name: result.name, test_count: result.test_count, is_package: 0, is_unmapped: true, from_price: result.from_price }
      : { test_master_id: result.id, name: result.name, test_count: result.test_count, is_package: 0, from_price: result.from_price };
  };

  const handleAdd = (result) => {
    const item = toSelectionItem(result);
    isTestSelected(item) ? removeTest(item) : addTest(item);
  };

  const handleChipClick = (chipLabel) => { setQuery(chipLabel); setOpen(true); };

  // location.city is filled either by manual selection or by context's
  // on-load reverse-geocode. While geocoding is still running (location
  // exists but no city yet) show "Locating…" so the user knows it's working.
  const locationLabel = location?.city
    ? location.city
    : location
    ? "Locating…"
    : "Add location";

  return (
    <>
      <div ref={containerRef} className="relative">
        <div className="flex gap-2 sm:gap-3">
          {/* Test search input */}
          <div className="relative flex-1 min-w-0">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 text-[#13B58E]" size={16} />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
              onFocus={() => setOpen(true)}
              placeholder="Search tests, packages..."
              className="w-full h-12 pl-9 sm:pl-11 pr-3 rounded-full border border-gray-200 bg-white text-[13px] sm:text-[14px] text-[#1A1A1A] placeholder:text-[#9AA6A4] focus:outline-none focus:ring-1 focus:ring-[#13B58E]"
            />
          </div>

          {/* Location button — compact on mobile, full label on sm+ */}
          <button
            onClick={() => setShowLocationModal(true)}
            className="h-12 shrink-0 rounded-full bg-[#13B58E] text-white font-semibold flex items-center gap-1.5 hover:bg-[#0fa07d] transition px-3 sm:px-5 text-[12px] sm:text-[13px]"
          >
            <MapPin size={14} className="shrink-0" />
            <span className="truncate max-w-[70px] sm:max-w-[120px]">{locationLabel}</span>
          </button>
        </div>

        {/* Popular test chips */}
        <div className="flex flex-wrap items-center gap-1.5 mt-3">
          <span className="text-[11px] text-[#6B7A99]">Popular:</span>
          {POPULAR_CHIPS.map((chip) => (
            <button
              key={chip}
              onClick={() => handleChipClick(chip)}
              className="text-[11px] px-2.5 py-1 rounded-full border border-gray-200 text-[#3D4945] hover:border-[#13B58E] hover:text-[#13B58E] transition"
            >
              {chip}
            </button>
          ))}
        </div>

        {/* Search dropdown */}
        {open && query.trim() && (
          <div className="absolute left-0 right-0 mt-3 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-30">
            {loading && (
              <div className="flex items-center justify-center gap-2 px-4 py-5 text-[13px] text-[#6B7A99]">
                <Loader2 size={15} className="animate-spin" /> Searching...
              </div>
            )}
            {!loading && error && <p className="px-4 py-4 text-[13px] text-red-500">{error}</p>}
            {!loading && !error && results.length === 0 && (
              <p className="px-4 py-4 text-[13px] text-[#6B7A99]">No tests or packages found for "{query}".</p>
            )}
            {!loading && !error && results.length > 0 && (
              <div className="max-h-[360px] overflow-y-auto">
                {tests.length > 0 && (
                  <>
                    <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-[#6B7A99]">
                      Tests <span className="text-[#13B58E]">{tests.length}</span>
                    </p>
                    {tests.map((result) => (
                      <SearchResultRow
                        key={`test:${result.source ?? ""}:${result.id ?? result.name}`}
                        result={result}
                        selected={isTestSelected(toSelectionItem(result))}
                        onAdd={() => handleAdd(result)}
                      />
                    ))}
                  </>
                )}
                {packages.length > 0 && (
                  <>
                    <p className="px-4 pt-3 pb-1 text-[11px] font-bold text-[#6B7A99]">
                      Packages <span className="text-[#13B58E]">{packages.length}</span>
                    </p>
                    {packages.map((result) => (
                      <SearchResultRow
                        key={`package:${result.id ?? result.name}`}
                        result={result}
                        selected={isTestSelected(toSelectionItem(result))}
                        onAdd={() => handleAdd(result)}
                      />
                    ))}
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Location modal */}
      {showLocationModal && <LocationModal onClose={() => setShowLocationModal(false)} />}
    </>
  );
};

export default LabSearchBar;
